import { useState, useEffect, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/lib/auth';
import { TopNav } from '@/components/layout/TopNav';
import { queuesApi, type PostQueue, type SlotContentType } from '@/lib/api';
import { useAppStore } from '@/stores/appStore';
import { cn } from '@/lib/utils';
import { Clock, Plus, Trash2, Pause, Play, Loader2 } from 'lucide-react';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const CONTENT_TYPE_OPTIONS: { value: SlotContentType; label: string }[] = [
  { value: 'image', label: 'Image' },
  { value: 'video', label: 'Video' },
  { value: 'text', label: 'Text only' },
  { value: 'any', label: 'Any' },
];

function defaultTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone ?? 'UTC';
  } catch {
    return 'UTC';
  }
}

function formatTime(timeMinutes: number): string {
  const h = Math.floor(timeMinutes / 60);
  const m = timeMinutes % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

function parseTime(value: string): number {
  const [h, m] = value.split(':').map(Number);
  return (h || 0) * 60 + (m || 0);
}

export function Queues() {
  const { getToken } = useAuth();
  const { currentWorkspace } = useAppStore();
  const qc = useQueryClient();

  const [selectedId, setSelectedId] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['queues', currentWorkspace?.id],
    queryFn: async () => {
      const token = await getToken();
      return queuesApi.list(token!, currentWorkspace!.id);
    },
    enabled: !!currentWorkspace,
  });

  const queues = data?.queues ?? [];
  const selected = useMemo(
    () => queues.find((q) => q.id === selectedId) ?? queues[0] ?? null,
    [queues, selectedId]
  );

  useEffect(() => {
    if (!selectedId && queues[0]) setSelectedId(queues[0].id);
  }, [queues, selectedId]);

  const createQueue = useMutation({
    mutationFn: async (name: string) => {
      const token = await getToken();
      return queuesApi.create(token!, {
        workspace_id: currentWorkspace!.id,
        name,
        timezone: defaultTimezone(),
      });
    },
    onSuccess: (resp) => {
      qc.invalidateQueries({ queryKey: ['queues', currentWorkspace?.id] });
      setSelectedId(resp.queue.id);
    },
  });

  const deleteQueue = useMutation({
    mutationFn: async (id: string) => {
      const token = await getToken();
      return queuesApi.delete(token!, id);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['queues', currentWorkspace?.id] });
      qc.invalidateQueries({ queryKey: ['posts', currentWorkspace?.id] });
      setSelectedId(null);
    },
  });

  const togglePause = useMutation({
    mutationFn: async (q: PostQueue) => {
      const token = await getToken();
      return queuesApi.update(token!, q.id, { paused: !q.paused });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['queues', currentWorkspace?.id] }),
  });

  if (!currentWorkspace) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <p className="text-sm text-muted-foreground">Select a workspace to manage queues.</p>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-hidden flex flex-col">
      <TopNav
        title="Queues"
        action={{
          label: createQueue.isPending ? 'Creating...' : 'New Queue',
          onClick: () => {
            const name = window.prompt('Queue name', 'Default queue');
            if (name?.trim()) createQueue.mutate(name.trim());
          },
        }}
      />

      <div className="flex flex-col md:flex-row flex-1 overflow-hidden">
        {/* Queue list */}
        <div className="w-full md:w-64 shrink-0 max-h-48 md:max-h-none border-b md:border-b-0 md:border-r border-hairline overflow-y-auto p-3 space-y-1.5">
          {isLoading ? (
            <div className="space-y-1.5">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="h-14 bg-muted/40 rounded-m animate-pulse" />
              ))}
            </div>
          ) : queues.length === 0 ? (
            <div className="text-center px-3 py-6 text-xs text-muted-foreground">
              No queues yet. Create one to define a recurring posting cadence.
            </div>
          ) : (
            queues.map((q) => (
              <button
                key={q.id}
                onClick={() => setSelectedId(q.id)}
                className={cn(
                  'group w-full text-left px-3 py-2.5 rounded-m transition-colors ring-1',
                  selected?.id === q.id
                    ? 'bg-accent/15 ring-accent/30'
                    : 'ring-hairline hover:bg-tertiary/40'
                )}
              >
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-foreground truncate">{q.name}</p>
                  {q.paused ? (
                    <span className="text-[10px] text-warning bg-warning/10 px-1.5 py-0.5 rounded-s">paused</span>
                  ) : null}
                </div>
                <p className="text-xs text-muted-foreground truncate mt-0.5">
                  <Clock className="inline h-3 w-3 mr-1" />
                  {q.slots?.length ?? 0} slot{(q.slots?.length ?? 0) === 1 ? '' : 's'} · {q.timezone}
                </p>
              </button>
            ))
          )}
        </div>

        {/* Slot editor */}
        <div className="flex-1 min-h-0 overflow-y-auto p-4 md:p-6">
          {selected ? (
            <QueueEditor
              key={selected.id}
              queue={selected}
              onPause={() => togglePause.mutate(selected)}
              onDelete={() => {
                // Deleting a queue unschedules its waiting posts back to drafts.
                const waiting =
                  qc
                    .getQueryData<{ posts: { status: string }[] }>(['queue-posts', selected.id])
                    ?.posts.filter((p) => p.status === 'scheduled').length ?? 0;
                const note = waiting
                  ? `\n\n${waiting} upcoming post${waiting === 1 ? '' : 's'} in this queue will be unscheduled and moved back to Drafts.`
                  : '';
                if (window.confirm(`Delete queue "${selected.name}"?${note}`)) {
                  deleteQueue.mutate(selected.id);
                }
              }}
            />
          ) : (
            <div className="text-center text-sm text-muted-foreground mt-12">
              Create a queue to get started.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

interface DraftSlot {
  day_of_week: number;
  time_minutes: number;
  content_types: SlotContentType[];
}

function QueueEditor({
  queue,
  onPause,
  onDelete,
}: {
  queue: PostQueue;
  onPause: () => void;
  onDelete: () => void;
}) {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  const { currentWorkspace } = useAppStore();

  const [slots, setSlots] = useState<DraftSlot[]>(() =>
    (queue.slots ?? []).map((s) => ({
      day_of_week: s.day_of_week,
      time_minutes: s.time_minutes,
      content_types: s.content_types?.length ? s.content_types : ['any'],
    }))
  );

  // What's actually sitting in this queue and when each post goes out.
  const { data: queuePostsData } = useQuery({
    queryKey: ['queue-posts', queue.id],
    queryFn: async () => {
      const token = await getToken();
      return queuesApi.getPosts(token!, queue.id);
    },
  });
  const upcoming = queuePostsData?.posts ?? [];

  const save = useMutation({
    mutationFn: async () => {
      const token = await getToken();
      return queuesApi.replaceSlots(token!, queue.id, slots);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['queues', currentWorkspace?.id] }),
  });

  const addSlot = () =>
    setSlots((prev) => [
      ...prev,
      { day_of_week: 1, time_minutes: 9 * 60, content_types: ['any'] },
    ]);

  const updateSlot = (idx: number, patch: Partial<DraftSlot>) =>
    setSlots((prev) => prev.map((s, i) => (i === idx ? { ...s, ...patch } : s)));

  const removeSlot = (idx: number) =>
    setSlots((prev) => prev.filter((_, i) => i !== idx));

  const toggleType = (idx: number, type: SlotContentType) =>
    setSlots((prev) =>
      prev.map((s, i) => {
        if (i !== idx) return s;
        // Selecting "any" clears the others (and vice-versa).
        if (type === 'any') return { ...s, content_types: ['any'] };
        const next = s.content_types.filter((t) => t !== 'any');
        return {
          ...s,
          content_types: next.includes(type)
            ? next.filter((t) => t !== type) || ['any']
            : [...next, type],
        };
      })
    );

  const sortedSlots = useMemo(
    () =>
      [...slots]
        .map((s, idx) => ({ ...s, idx }))
        .sort((a, b) =>
          a.day_of_week === b.day_of_week ? a.time_minutes - b.time_minutes : a.day_of_week - b.day_of_week
        ),
    [slots]
  );

  return (
    <div className="max-w-3xl space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-xl font-semibold text-foreground">{queue.name}</h2>
          <p className="text-xs text-muted-foreground mt-1">
            Posts added to this queue auto-schedule into the next matching slot ({queue.timezone}).
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={onPause}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-m ring-1 ring-hairline hover:bg-tertiary/40 transition-colors"
          >
            {queue.paused ? <Play className="h-3.5 w-3.5" /> : <Pause className="h-3.5 w-3.5" />}
            {queue.paused ? 'Resume' : 'Pause'}
          </button>
          <button
            onClick={onDelete}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-m text-danger ring-1 ring-hairline hover:bg-danger/10 transition-colors"
          >
            <Trash2 className="h-3.5 w-3.5" />
            Delete
          </button>
        </div>
      </div>

      <div className="bg-card ring-1 ring-hairline rounded-l shadow-kumo-m overflow-hidden">
        <div className="px-4 py-3 border-b border-hairline flex items-center justify-between">
          <p className="text-sm font-medium text-foreground">Recurring slots</p>
          <button
            onClick={addSlot}
            className="flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-s bg-accent/20 text-accent ring-1 ring-accent/30 hover:bg-accent/30 transition-colors"
          >
            <Plus className="h-3.5 w-3.5" />
            Add slot
          </button>
        </div>

        {sortedSlots.length === 0 ? (
          <div className="px-4 py-8 text-center text-xs text-muted-foreground">
            No slots yet. Add at least one day/time so posts can be slotted in.
          </div>
        ) : (
          <div className="divide-y divide-hairline">
            {sortedSlots.map(({ idx, day_of_week, time_minutes, content_types }) => (
              <div key={idx} className="px-4 py-3 flex flex-wrap items-center gap-3">
                <select
                  value={day_of_week}
                  onChange={(e) => updateSlot(idx, { day_of_week: Number(e.target.value) })}
                  className="bg-input border border-border rounded-s px-2 py-1 text-xs text-foreground"
                >
                  {DAYS.map((d, i) => (
                    <option key={i} value={i}>{d}</option>
                  ))}
                </select>
                <input
                  type="time"
                  value={formatTime(time_minutes)}
                  onChange={(e) => updateSlot(idx, { time_minutes: parseTime(e.target.value) })}
                  className="bg-input border border-border rounded-s px-2 py-1 text-xs text-foreground"
                />
                <div className="flex flex-wrap items-center gap-1.5 flex-1 min-w-0">
                  {CONTENT_TYPE_OPTIONS.map((opt) => {
                    const active = content_types.includes(opt.value);
                    return (
                      <button
                        key={opt.value}
                        onClick={() => toggleType(idx, opt.value)}
                        className={cn(
                          'px-2 py-0.5 text-[11px] rounded-s ring-1 transition-colors',
                          active
                            ? 'bg-accent/20 text-accent ring-accent/30'
                            : 'text-muted-foreground ring-hairline hover:bg-tertiary/40'
                        )}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
                <button
                  onClick={() => removeSlot(idx)}
                  className="p-1 text-muted-foreground hover:text-danger rounded-s hover:bg-danger/10 transition-colors"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="flex justify-end">
        <button
          onClick={() => save.mutate()}
          disabled={save.isPending}
          className="flex items-center gap-2 px-4 py-2 bg-accent text-accent-foreground rounded-m text-sm hover:bg-accent/90 disabled:opacity-50 shadow-kumo-m"
        >
          {save.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
          Save slots
        </button>
      </div>

      <div className="bg-card ring-1 ring-hairline rounded-l shadow-kumo-m overflow-hidden">
        <div className="px-4 py-3 border-b border-hairline">
          <p className="text-sm font-medium text-foreground">
            Upcoming in this queue
            {upcoming.length > 0 && (
              <span className="ml-2 text-xs text-muted-foreground">{upcoming.length} post{upcoming.length === 1 ? '' : 's'}</span>
            )}
          </p>
        </div>
        {upcoming.length === 0 ? (
          <div className="px-4 py-8 text-center text-xs text-muted-foreground">
            Nothing queued yet. Add posts from the Composer or Bulk Posting.
          </div>
        ) : (
          <div className="divide-y divide-hairline">
            {upcoming.map((post) => (
              <div key={post.id} className="px-4 py-3 flex items-center gap-3">
                <Clock className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                <p className="text-xs text-foreground flex-1 min-w-0 line-clamp-1">{post.content}</p>
                <span className="text-[11px] text-muted-foreground shrink-0 tabular-nums">
                  {post.scheduled_at
                    ? new Date(post.scheduled_at).toLocaleString(undefined, {
                        weekday: 'short',
                        month: 'short',
                        day: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit',
                      })
                    : '—'}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
